import { useState, useCallback, useMemo, useEffect } from "react"
import { notify } from "shared/ui/Notifications/notify"
import { Template, Slide } from "types/ReportingDashboard/reporting.types"
import {
  SlideElement,
  DataElementConfig,
} from "types/ReportingDashboard/elements.types"
import { templateOperations, templateStateUtils } from "utils/templateUtils"

/**
 * Template modal data interface
 */
interface TemplateModalData {
  action: "create" | "replace"
  name: string
}

/**
 * Props for the template management hook
 */
interface UseTemplateManagementProps {
  currentSlides: Slide[]
  currentTemplates: Record<string, Template>
  activeSlideId: string | null
  activeSlide: Slide | null
  onDashboardConfigChange: (config: {
    slides: Slide[]
    templates: Record<string, Template>
  }) => void
  getCurrentBackgroundColor: () => string
  getElementsToSave: () => SlideElement[]
  getTableConfigsToSave: () => Record<string, DataElementConfig>
}

/**
 * Template management state interface
 */
interface TemplateManagementState {
  activeTemplate: string | null
  isEditingTemplate: boolean
  templateModalVisible: boolean
  templateModalMode: "create" | "edit"
  editingTemplate: string | null
  originalTemplateState: Template | null
  saveSourceIsTemplate: boolean
}

/**
 * Custom hook for managing template operations and state
 */
export const useTemplateManagement = ({
  currentSlides,
  currentTemplates,
  activeSlideId,
  activeSlide,
  onDashboardConfigChange,
  getCurrentBackgroundColor,
  getElementsToSave,
  getTableConfigsToSave,
}: UseTemplateManagementProps) => {
  // ========================================================================================
  // STATE
  // ========================================================================================

  const [state, setState] = useState<TemplateManagementState>({
    activeTemplate: null,
    isEditingTemplate: false,
    templateModalVisible: false,
    templateModalMode: "create",
    editingTemplate: null,
    originalTemplateState: null,
    saveSourceIsTemplate: false,
  })

  // ========================================================================================
  // COMPUTED VALUES
  // ========================================================================================

  /**
   * Name of the template currently being edited
   */
  const templateBeingEditedName = useMemo(
    () =>
      state.isEditingTemplate &&
      state.editingTemplate &&
      currentTemplates[state.editingTemplate]
        ? currentTemplates[state.editingTemplate].name
        : "",
    [state.isEditingTemplate, state.editingTemplate, currentTemplates]
  )

  // ========================================================================================
  // STATE SETTERS
  // ========================================================================================

  const setActiveTemplate = useCallback((template: string | null) => {
    setState((prev) => ({ ...prev, activeTemplate: template }))
  }, [])

  const setIsEditingTemplate = useCallback((isEditing: boolean) => {
    setState((prev) => ({ ...prev, isEditingTemplate: isEditing }))
  }, [])

  const setTemplateModalVisible = useCallback((visible: boolean) => {
    setState((prev) => ({ ...prev, templateModalVisible: visible }))
  }, [])

  const setTemplateModalMode = useCallback((mode: "create" | "edit") => {
    setState((prev) => ({ ...prev, templateModalMode: mode }))
  }, [])

  const setEditingTemplate = useCallback((template: string | null) => {
    setState((prev) => ({ ...prev, editingTemplate: template }))
  }, [])

  const setOriginalTemplateState = useCallback((template: Template | null) => {
    setState((prev) => ({ ...prev, originalTemplateState: template }))
  }, [])

  const setSaveSourceIsTemplate = useCallback((isTemplate: boolean) => {
    setState((prev) => ({ ...prev, saveSourceIsTemplate: isTemplate }))
  }, [])

  // ========================================================================================
  // TEMPLATE OPERATIONS
  // ========================================================================================

  /**
   * Creates a new template
   */
  const createTemplate = useCallback(
    (templateData: TemplateModalData): void => {
      const elementsToSave = getElementsToSave()
      const tableConfigsToSave = getTableConfigsToSave()
      const currentBgColor = getCurrentBackgroundColor()

      const newTemplate = templateOperations.createNewTemplate(
        templateData.name,
        elementsToSave,
        tableConfigsToSave,
        currentBgColor
      )

      const updatedTemplates = {
        ...currentTemplates,
        [newTemplate.id]: newTemplate,
      }

      // Restore original template if editing
      if (
        state.saveSourceIsTemplate &&
        state.editingTemplate &&
        state.originalTemplateState
      ) {
        updatedTemplates[state.editingTemplate] = state.originalTemplateState
      }

      // Update active slide to use new template
      let updatedSlides = [...currentSlides]
      if (activeSlide) {
        updatedSlides = currentSlides.map((slide: Slide) =>
          slide.id === activeSlideId
            ? {
                ...slide,
                templateId: newTemplate.id,
                slideElements: [],
                slideTableConfigs: {},
                backgroundColor: undefined,
              }
            : slide
        )
      }

      onDashboardConfigChange({
        slides: updatedSlides,
        templates: updatedTemplates,
      })
      setActiveTemplate(newTemplate.id)
      notify.toast.success(`Template "${newTemplate.name}" created.`)
    },
    [
      getElementsToSave,
      getTableConfigsToSave,
      getCurrentBackgroundColor,
      currentTemplates,
      currentSlides,
      activeSlide,
      activeSlideId,
      onDashboardConfigChange,
      state.saveSourceIsTemplate,
      state.editingTemplate,
      state.originalTemplateState,
    ]
  )

  /**
   * Replaces an existing template
   */
  const replaceTemplate = useCallback(
    (templateData: TemplateModalData): void => {
      const templateIdToReplace = templateData.name

      if (!currentTemplates[templateIdToReplace]) {
        notify.toast.error("Template to replace not found.")
        return
      }

      const elementsToSave = getElementsToSave()
      const tableConfigsToSave = getTableConfigsToSave()
      const currentBgColor = getCurrentBackgroundColor()

      const finalElementsForTemplate = state.saveSourceIsTemplate
        ? elementsToSave
        : elementsToSave.map((el) => ({ ...el, isTemplateElement: true }))

      const updatedTemplate: Template = {
        ...currentTemplates[templateIdToReplace],
        elements: finalElementsForTemplate,
        tableConfigs: tableConfigsToSave,
        backgroundColor: currentBgColor,
        timestamp: Date.now(),
      }

      const updatedTemplates = {
        ...currentTemplates,
        [templateIdToReplace]: updatedTemplate,
      }

      // Update slide if not editing from template
      let updatedSlides = [...currentSlides]
      if (
        !state.saveSourceIsTemplate &&
        activeSlide &&
        activeSlide.id === activeSlideId
      ) {
        updatedSlides = currentSlides.map((s: Slide) => {
          if (s.id === activeSlideId) {
            return {
              ...s,
              templateId: templateIdToReplace,
              slideElements: [],
              slideTableConfigs: {},
              backgroundColor: undefined,
            }
          }
          return s
        })
      }

      onDashboardConfigChange({
        slides: updatedSlides,
        templates: updatedTemplates,
      })
      setActiveTemplate(templateIdToReplace)
      notify.toast.success(`Template "${updatedTemplate.name}" updated.`)
    },
    [
      currentTemplates,
      getElementsToSave,
      getTableConfigsToSave,
      getCurrentBackgroundColor,
      currentSlides,
      activeSlide,
      activeSlideId,
      onDashboardConfigChange,
      state.saveSourceIsTemplate,
    ]
  )

  /**
   * Selects a template for the current slide
   */
  const selectTemplate = useCallback(
    (templateKeyToSelect: string | null): void => {
      if (
        templateKeyToSelect !== undefined &&
        (templateKeyToSelect === null || currentTemplates[templateKeyToSelect])
      ) {
        if (state.isEditingTemplate) {
          notify.toast.warning(
            "Finish editing the current template before selecting another."
          )
          return
        }

        let updatedSlides = [...currentSlides]
        if (activeSlide) {
          updatedSlides = currentSlides.map((slide: Slide) =>
            slide.id === activeSlideId
              ? templateOperations.updateSlideWithTemplate(
                  slide,
                  templateKeyToSelect,
                  currentTemplates
                )
              : slide
          )
        }

        onDashboardConfigChange({
          slides: updatedSlides,
          templates: currentTemplates,
        })
        setActiveTemplate(templateKeyToSelect)
      }
    },
    [
      currentTemplates,
      currentSlides,
      activeSlideId,
      activeSlide,
      state.isEditingTemplate,
      onDashboardConfigChange,
    ]
  )

  /**
   * Deletes a template and migrates its elements to affected slides
   */
  const deleteTemplate = useCallback(
    (templateKey: string): void => {
      if (!currentTemplates[templateKey]) return

      const templateName = currentTemplates[templateKey].name
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [templateKey]: _, ...remainingTemplates } = currentTemplates

      const updatedSlides = currentSlides.map((slide: Slide) => {
        if (slide.templateId !== templateKey) return slide

        const deletedTemplate = currentTemplates[templateKey]
        const migratedElements = templateOperations.migrateTemplateElements(
          deletedTemplate?.elements || []
        )

        return {
          ...slide,
          templateId: null,
          slideElements: [...migratedElements, ...(slide.slideElements || [])],
          slideTableConfigs: {
            ...(deletedTemplate?.tableConfigs || {}),
            ...(slide.slideTableConfigs || {}),
          },
          backgroundColor:
            slide.backgroundColor ||
            deletedTemplate?.backgroundColor ||
            "#ffffff",
        }
      })

      onDashboardConfigChange({
        slides: updatedSlides,
        templates: remainingTemplates,
      })

      if (state.activeTemplate === templateKey) setActiveTemplate(null)
      if (state.isEditingTemplate && state.editingTemplate === templateKey) {
        templateStateUtils.resetTemplateModalState({
          setIsEditingTemplate,
          setEditingTemplate,
          setTemplateModalVisible,
          setSaveSourceIsTemplate,
          setOriginalTemplateState,
        })
      }
      notify.toast.success(`Template "${templateName}" deleted.`)
    },
    [
      currentTemplates,
      currentSlides,
      onDashboardConfigChange,
      state.activeTemplate,
      state.isEditingTemplate,
      state.editingTemplate,
    ]
  )

  // ========================================================================================
  // MODAL OPERATIONS
  // ========================================================================================

  /**
   * Opens the template editing modal for an existing template
   */
  const openEditTemplateModal = useCallback(
    (templateKey: string): void => {
      templateStateUtils.setupTemplateEditing(templateKey, currentTemplates, {
        setOriginalTemplateState,
        setTemplateModalMode,
        setEditingTemplate,
        setIsEditingTemplate,
      })
    },
    [currentTemplates]
  )

  /**
   * Opens the save template modal
   */
  const openSaveTemplateModal = useCallback((): void => {
    templateStateUtils.setupTemplateCreation(
      state.isEditingTemplate,
      state.editingTemplate,
      {
        setSaveSourceIsTemplate,
        setTemplateModalMode,
        setEditingTemplate,
        setTemplateModalVisible,
      }
    )
  }, [state.isEditingTemplate, state.editingTemplate])

  /**
   * Handles template modal cancellation with state restoration
   */
  const handleTemplateModalCancel = useCallback((): void => {
    if (
      state.isEditingTemplate &&
      state.editingTemplate &&
      state.originalTemplateState
    ) {
      const restoredTemplates = {
        ...currentTemplates,
        [state.editingTemplate]: state.originalTemplateState,
      }
      onDashboardConfigChange({
        slides: currentSlides,
        templates: restoredTemplates,
      })
    }

    templateStateUtils.resetTemplateModalState({
      setIsEditingTemplate,
      setEditingTemplate,
      setTemplateModalVisible,
      setSaveSourceIsTemplate,
      setOriginalTemplateState,
    })
  }, [
    state.isEditingTemplate,
    state.editingTemplate,
    state.originalTemplateState,
    currentSlides,
    currentTemplates,
    onDashboardConfigChange,
  ])

  /**
   * Handles template modal save operations
   */
  const handleTemplateModalSave = useCallback(
    (templateData: TemplateModalData): void => {
      if (templateData.action === "create") {
        createTemplate(templateData)
      } else {
        replaceTemplate(templateData)
      }

      templateStateUtils.resetTemplateModalState({
        setIsEditingTemplate,
        setEditingTemplate,
        setTemplateModalVisible,
        setSaveSourceIsTemplate,
        setOriginalTemplateState,
      })
    },
    [createTemplate, replaceTemplate]
  )

  // ========================================================================================
  // EFFECTS
  // ========================================================================================

  /**
   * Updates activeTemplate based on the active slide's templateId
   */
  useEffect(() => {
    const currentActiveSlideObj = currentSlides.find(
      (s: Slide) => s.id === activeSlideId
    )
    if (currentActiveSlideObj) {
      setActiveTemplate(currentActiveSlideObj.templateId)
    } else if (currentSlides.length > 0 && !activeSlideId) {
      setActiveTemplate(currentSlides[0].templateId || null)
    }
  }, [activeSlideId, currentSlides])

  /**
   * Listen for global save template events
   */
  useEffect(() => {
    const handleSaveTemplateChanges = (): void => {
      if (state.isEditingTemplate && state.editingTemplate)
        openSaveTemplateModal()
    }

    window.addEventListener("saveTemplateChanges", handleSaveTemplateChanges)
    return () =>
      window.removeEventListener(
        "saveTemplateChanges",
        handleSaveTemplateChanges
      )
  }, [openSaveTemplateModal, state.isEditingTemplate, state.editingTemplate])

  // ========================================================================================
  // RETURN
  // ========================================================================================

  return {
    // State
    activeTemplate: state.activeTemplate,
    isEditingTemplate: state.isEditingTemplate,
    templateModalVisible: state.templateModalVisible,
    templateModalMode: state.templateModalMode,
    editingTemplate: state.editingTemplate,
    originalTemplateState: state.originalTemplateState,
    templateBeingEditedName,

    // Operations
    selectTemplate,
    deleteTemplate,
    openEditTemplateModal,
    openSaveTemplateModal,
    handleTemplateModalCancel,
    handleTemplateModalSave,
    setActiveTemplate,
  }
}
